import { Box, Flex, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  items: TocItem[];
}

export type { TocItem };

const TocLink = ({ item, isActive }: { item: TocItem; isActive: boolean }) => (
  <a
    href={`#${item.id}`}
    onClick={(e) => {
      e.preventDefault();
      const el = document.getElementById(item.id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }}
    style={{ textDecoration: 'none' }}
  >
    <Text
      pl={item.level === 3 ? 6 : 3}
      py={1}
      textStyle="sm"
      lineHeight="1.4"
      color={isActive ? 'purple.400' : 'whiteAlpha.600'}
      fontWeight={isActive ? '600' : '400'}
      borderLeftWidth="2px"
      borderColor={isActive ? 'purple.400' : 'transparent'}
      ml="-1px"
      transition="all 150ms"
      _hover={{ color: 'whiteAlpha.900' }}
      css={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
    >
      {item.text}
    </Text>
  </a>
);

export const TableOfContents = ({ items }: TableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <Box
      as="nav"
      position="sticky"
      top="100px"
      w="220px"
      flexShrink={0}
      display={{ base: 'none', xl: 'block' }}
    >
      <Text textStyle="badge" textTransform="uppercase" color="whiteAlpha.500" mb={3}>
        Contents
      </Text>
      <Flex direction="column" gap={1} borderLeftWidth="1px" borderColor="whiteAlpha.200">
        {items.map((item) => (
          <TocLink key={item.id} item={item} isActive={activeId === item.id} />
        ))}
      </Flex>
    </Box>
  );
};
